import {
  isUnoptimizedServer,
  type McpServer,
  type McpTool,
  type Mode,
  type ToolGroup,
} from "./types";

// Selection identity is the tool's version-less qualified name ("Toolkit.Tool"),
// never the fully-qualified name with an "@version" suffix. This matches what the
// gateway backend stores ("any version of this tool") and keeps a tool selected
// across version upgrades. `fully_qualified_name` is used only for display.

export function applyToggleTool(
  selected: Set<string>,
  fqn: string,
  mode: Mode
): Set<string> {
  if (mode === "single") {
    return selected.has(fqn) ? new Set() : new Set([fqn]);
  }
  const next = new Set(selected);
  if (next.has(fqn)) {
    next.delete(fqn);
  } else {
    next.add(fqn);
  }
  return next;
}

export function applyToggleServer(
  selected: Set<string>,
  fqns: string[]
): Set<string> {
  if (fqns.length === 0) {
    return selected;
  }
  const allSelected = fqns.every((f) => selected.has(f));
  const next = new Set(selected);
  for (const f of fqns) {
    if (allSelected) {
      next.delete(f);
    } else {
      next.add(f);
    }
  }
  return next;
}

export function applyRemoveTools(
  selected: Set<string>,
  fqns: string[]
): Set<string> {
  if (fqns.length === 0 || selected.size === 0) {
    return selected;
  }
  const next = new Set(selected);
  for (const f of fqns) {
    next.delete(f);
  }
  return next.size === selected.size ? selected : next;
}

function collectUnoptimizedFqns(servers: McpServer[]): string[] {
  const fqns: string[] = [];
  for (const server of servers) {
    if (isUnoptimizedServer(server)) {
      fqns.push(...fqnsForServer(server));
    }
  }
  return fqns;
}

/**
 * Compute the selection after a "select all visible" toggle.
 *
 * - Nothing visible → unchanged (the caller treats this as a no-op).
 * - Not all visible selected → add every visible tool.
 * - All visible already selected → remove every visible tool. When
 *   `sweepHiddenUnoptimized` is set, also drop tools belonging to hidden
 *   unoptimized servers, so an off-screen unoptimized selection can't survive a
 *   "clear" the user believes cleared everything.
 */
export function applyToggleVisibleTools({
  selected,
  visibleToolFqns,
  allVisibleSelected,
  servers,
  sweepHiddenUnoptimized,
}: {
  selected: Set<string>;
  visibleToolFqns: string[];
  allVisibleSelected: boolean;
  servers: McpServer[];
  sweepHiddenUnoptimized: boolean;
}): Set<string> {
  if (visibleToolFqns.length === 0) {
    return selected;
  }
  const next = new Set(selected);
  if (!allVisibleSelected) {
    for (const fqn of visibleToolFqns) {
      next.add(fqn);
    }
    return next;
  }
  for (const fqn of visibleToolFqns) {
    next.delete(fqn);
  }
  if (sweepHiddenUnoptimized) {
    for (const fqn of collectUnoptimizedFqns(servers)) {
      next.delete(fqn);
    }
  }
  return next;
}

/** How many of `tools` are in the current selection (by version-less FQN). */
export function countSelectedTools(
  tools: McpTool[],
  selected: Set<string>
): number {
  let count = 0;
  for (const tool of tools) {
    if (selected.has(tool.qualified_name)) {
      count += 1;
    }
  }
  return count;
}

export function buildValidFqns(servers: McpServer[]): Set<string> {
  const set = new Set<string>();
  for (const server of servers) {
    for (const tool of server.tools) {
      set.add(tool.qualified_name);
    }
  }
  return set;
}

export function fqnsForServer(server?: McpServer): string[] {
  return server ? server.tools.map((t) => t.qualified_name) : [];
}

export function pruneSelectionToCatalog(
  selected: Set<string>,
  validFqns: Set<string>
): Set<string> {
  if (validFqns.size === 0) {
    return selected;
  }
  for (const fqn of selected) {
    if (!validFqns.has(fqn)) {
      return new Set([...selected].filter((f) => validFqns.has(f)));
    }
  }
  return selected;
}

/**
 * Reconcile selection against a catalog that may still be loading asynchronously.
 *
 * - Empty catalog → keep the raw selection (nothing to validate against yet).
 * - Known-valid FQNs → keep.
 * - FQNs previously seen in the catalog but now absent → drop (catalog drift).
 * - Unknown FQNs → keep while `deferUnknownPruning` (catalog still growing), otherwise drop.
 */
export function reconcileSelectionWithCatalog(
  selected: Set<string>,
  validFqns: Set<string>,
  everValidFqns: ReadonlySet<string>,
  deferUnknownPruning: boolean
): Set<string> {
  if (validFqns.size === 0) {
    return selected;
  }

  const next = new Set<string>();
  for (const fqn of selected) {
    if (validFqns.has(fqn)) {
      next.add(fqn);
      continue;
    }
    if (everValidFqns.has(fqn)) {
      continue;
    }
    if (deferUnknownPruning) {
      next.add(fqn);
    }
  }

  return setsEqual(next, selected) ? selected : next;
}

export function setsEqual(a: Set<string>, b: Set<string>) {
  return a.size === b.size && [...a].every((value) => b.has(value));
}

export type SelectedCatalogState = {
  groups: ToolGroup[];
  toolCount: number;
};

export function deriveSelectedCatalogState(
  servers: McpServer[],
  selected: Set<string>
): SelectedCatalogState {
  const groups: ToolGroup[] = [];
  let toolCount = 0;

  if (selected.size === 0) {
    return { groups, toolCount };
  }

  for (const server of servers) {
    let selectedTools: McpServer["tools"] | undefined;
    let selectedCount = 0;

    for (const tool of server.tools) {
      if (!selected.has(tool.qualified_name)) {
        continue;
      }
      selectedCount += 1;
      selectedTools ??= [];
      selectedTools.push(tool);
    }

    if (selectedCount === 0) {
      continue;
    }

    toolCount += selectedCount;
    groups.push({
      server,
      tools:
        selectedCount === server.tools.length
          ? server.tools
          : (selectedTools ?? []),
    });
  }

  return { groups, toolCount };
}
